import { Injectable, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { APP_SERVER_CONFIG, APP_SOCKET_URL } from '../../config/app-server.config';
import { DesktopBridgeService } from '../../shared/services/desktop-bridge.service';

@Injectable({
  providedIn: 'root'
})

export class SocketioService {
  private static readonly defaultRequestTimeoutMs = 30_000;
  private readonly sessionTokenKey = 'pc119.socketSessionToken';
  private readonly publicAuthenticationEvents = new Set([
    'auth', 'beginPasskeyAuthentication', 'finishPasskeyAuthentication',
    'createMobilePasskeyLogin', 'getMobilePasskeyRequest',
    'beginMobilePasskeyEnrollment', 'finishMobilePasskeyEnrollment',
    'beginMobilePasskeyLogin', 'finishMobilePasskeyLogin',
  ]);
  private socket: Socket;
  private authenticatedSocketId?: string;
  private lastAuthenticationResponse?: any;
  private authenticationRestorePromise?: Promise<any>;
  private authenticationRestoreDiagnostic = '起動時セッション復元はまだ実行されていません。';
  private readonly pendingRequestRejectors = new Set<(reason: Error) => void>();
  private readonly sessionExpiredListeners = new Set<() => void>();
  readonly connectionState = signal<'connected' | 'reconnecting' | 'authenticating' | 'unavailable'>('reconnecting');
  // Electronを起動し直した直後だけは、Windows Hello設定端末で保存済みトークンを使わせない。
  private desktopStartupAuthenticationSatisfied = false;

  constructor(private desktopBridge: DesktopBridgeService) {
    this.socket = io(APP_SOCKET_URL, {
      query: { appToken: APP_SERVER_CONFIG.appToken },
      reconnection: true,
      reconnectionAttempts: Infinity,
    });
    this.socket.on('disconnect', () => {
      this.authenticatedSocketId = undefined;
      this.lastAuthenticationResponse = undefined;
      this.connectionState.set('reconnecting');
      this.rejectPendingRequests(new Error('SOCKET_DISCONNECTED: サーバーとの接続が切断されました。'));
    });
    this.socket.on('connect', () => {
      void this.restoreAfterReconnect();
    });
    this.socket.io.on('reconnect_attempt', () => this.connectionState.set('reconnecting'));
    this.socket.io.on('reconnect_error', () => this.connectionState.set('unavailable'));
    this.socket.io.on('reconnect_failed', () => this.connectionState.set('unavailable'));
    this.socket.on('connect_error', () => this.connectionState.set('unavailable'));
  }

  connect() {
    this.socket.connect();
  }

  reconnectNow(): void {
    this.connectionState.set('reconnecting');
    if (this.socket.connected) this.socket.disconnect();
    this.socket.connect();
  }

  onSessionExpired(listener: () => void): () => void {
    this.sessionExpiredListeners.add(listener);
    return () => this.sessionExpiredListeners.delete(listener);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  hasStoredSession(): boolean {
    return this.readBrowserSessionToken() !== '';
  }

  async setAuthenticatedSession(sessionToken: string, isAdmin: boolean): Promise<void> {
    if (!sessionToken) return;
    try {
      sessionStorage.setItem(this.sessionTokenKey, sessionToken);
    } catch {
      // sessionStorageが利用できない環境でも、現在のSocket上のログインは維持する。
    }
    if (this.desktopBridge.isAvailable()) {
      const persisted = await this.persistCurrentSessionForDesktop();
      if (!persisted.status) console.warn(persisted.detail);
    } else {
      try {
        // iOS PWA は終了時にsessionStorageを破棄するため、サーバー側の
        // 有効期限内は次回起動時にも認証を復元できるようトークンを保持する。
        localStorage.setItem(this.sessionTokenKey, sessionToken);
      } catch {
        // 保存できない環境では、この起動中のログインだけを維持する。
      }
    }
    this.authenticatedSocketId = this.socket.id;
    this.lastAuthenticationResponse = { status: true, isAdmin };
    this.desktopStartupAuthenticationSatisfied = true;
  }

  async clearAuthenticatedSession(): Promise<void> {
    try {
      sessionStorage.removeItem(this.sessionTokenKey);
    } catch {
      // 保存領域が利用できない場合はSocket上の状態だけ解除する。
    }
    if (this.desktopBridge.isAvailable()) {
      const cleared = await this.desktopBridge.clearAuthSession();
      if (!cleared.ok) console.warn('Electronの保存済みログインセッションを削除できませんでした。');
    } else {
      try {
        localStorage.removeItem(this.sessionTokenKey);
      } catch {
        // 保存領域が利用できない場合も、Socket上の認証解除は続行する。
      }
    }
    this.authenticatedSocketId = undefined;
    this.lastAuthenticationResponse = undefined;
    this.desktopStartupAuthenticationSatisfied = false;
  }

  async restoreAuthentication(): Promise<any> {
    if (this.socket.connected && this.authenticatedSocketId === this.socket.id) {
      return this.lastAuthenticationResponse ?? { status: true, restored: true, isAdmin: false };
    }
    if (this.authenticationRestorePromise) return this.authenticationRestorePromise;

    // 現在のSocketが未認証または再接続された場合だけ、Electron IPCを含む
    // 保存セッションの読み出しを行う。認証済みSocketの業務APIごとにIPCを待つと、
    // IPC応答が滞った際にAPIイベント自体が送信されず画面が永久待機になる。
    const sessionToken = await this.readSessionToken();
    if (!sessionToken) return { status: false, code: 'NO_SESSION' };

    const restore = (async () => {
      await this.waitForSocketConnection(10000);
      const response = await this.emitRawWithTimeout('auth', { sessionToken }, 10000);
      if (!response?.status) {
        this.authenticationRestoreDiagnostic = `サーバーが保存セッションを拒否しました（${response?.code ?? 'UNKNOWN'}）。`;
        // Redis・ネットワーク障害は失効ではない。復旧まで端末トークンを保持する。
        if (response?.code === 'SESSION_EXPIRED') await this.clearAuthenticatedSession();
        const error = Object.assign(new Error(response?.detail ?? 'ログインセッションを復元できませんでした。'), {
          code: response?.code,
        });
        throw error;
      }
      this.authenticatedSocketId = this.socket.id;
      this.lastAuthenticationResponse = response;
      this.authenticationRestoreDiagnostic = '保存セッションを正常に復元しました。';
      return response;
    })();
    this.authenticationRestorePromise = restore;
    try {
      return await restore;
    } finally {
      if (this.authenticationRestorePromise === restore) {
        this.authenticationRestorePromise = undefined;
      }
    }
  }

  getAuthenticationRestoreDiagnostic(): string {
    return this.authenticationRestoreDiagnostic;
  }

  async persistCurrentSessionForDesktop(): Promise<{ status: boolean; detail: string }> {
    if (!this.desktopBridge.isAvailable()) {
      return { status: false, detail: 'Electron以外ではログインセッションを永続保存しません。' };
    }
    const sessionToken = this.readBrowserSessionToken();
    if (!sessionToken) {
      return { status: false, detail: '現在のログインセッションを確認できないため、デスクトップアプリへ保存できません。' };
    }
    const stored = await this.desktopBridge.saveAuthSession(sessionToken);
    if (!stored.ok) {
      return { status: false, detail: stored.message ?? `ログイン状態を保存できませんでした（${stored.errorCode ?? 'UNKNOWN'}）。` };
    }
    const verified = await this.desktopBridge.loadAuthSession();
    if (!verified.ok || verified.value !== sessionToken) {
      return { status: false, detail: verified.message ?? `保存したログイン状態を確認できませんでした（${verified.errorCode ?? 'UNKNOWN'}）。` };
    }
    return { status: true, detail: '現在のログイン状態をデスクトップアプリへ保存しました。' };
  }

  async logoutSession(): Promise<void> {
    const sessionToken = await this.readSessionToken();
    await this.clearAuthenticatedSession();
    try {
      if (sessionToken) {
        await this.emitRawWithTimeout('auth', { mode: 'logout', sessionToken }, 5000);
      }
    } catch (error) {
      console.warn('Server session logout failed:', error);
    } finally {
      // ACK不達時も、認証済みだったSocketを業務APIに再利用させない。
      // 保存トークンは消去済みなので、再接続後は未認証としてログインを待つ。
      this.socket.disconnect();
      this.socket.connect();
    }
  }

  // サーバーからのイベントを受け取る
  onEvent(event: string, callback: (...args: any[]) => void): void {
    this.socket.on(event, callback);
  }

  offEvent(event: string, callback: (...args: any[]) => void): void {
    this.socket.off(event, callback);
  }

  // サーバーにイベントを送信する
  async emitEvent<T>(event: string, data: T): Promise<any> {
    return this.emitEventWithAuthenticationFallback(event, data, SocketioService.defaultRequestTimeoutMs);
  }

  async emitEventWithTimeout<T>(event: string, data: T, timeoutMs: number): Promise<any> {
    return this.withOperationTimeout(
      this.emitEventWithAuthenticationFallback(event, data, timeoutMs),
      timeoutMs,
    );
  }

  private async emitEventWithAuthenticationFallback<T>(event: string, data: T, timeoutMs: number): Promise<any> {
    // 通常は現在のSocketがすでに認証済みなので、まず業務イベントを直接送る。
    // Electron IPCを含むセッション復元を先に待つと、IPCが応答しない場合に
    // Socket.IO側のtimeoutへ到達せず、呼び出し元のloadingが永久に解除されない。
    const response = await this.emitRawWithTimeout(event, data, timeoutMs);
    if (
      !this.publicAuthenticationEvents.has(event)
      && response?.code === 'AUTH_REQUIRED'
      && this.hasStoredSession()
    ) {
      await this.restoreAuthentication();
      return this.emitRawWithTimeout(event, data, timeoutMs);
    }
    return response;
  }

  private withOperationTimeout<T>(operation: Promise<T>, timeoutMs: number): Promise<T> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        settled = true;
        reject(new Error('SOCKET_REQUEST_TIMEOUT: サーバーからの応答を確認できませんでした。'));
      }, timeoutMs);

      operation.then((response) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(response);
      }, (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      });
    });
  }

  private emitRawWithTimeout<T>(event: string, data: T, timeoutMs: number): Promise<any> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (callback: () => void) => {
        if (settled) return;
        settled = true;
        this.pendingRequestRejectors.delete(onDisconnect);
        callback();
      };
      const onDisconnect = (reason: Error) => finish(() => reject(reason));
      this.pendingRequestRejectors.add(onDisconnect);
      this.socket.timeout(timeoutMs).emit(event, data, (error: Error | null, response: any) => {
        if (error) finish(() => reject(error));
        else finish(() => resolve(response));
      });
    });
  }

  private rejectPendingRequests(reason: Error): void {
    for (const reject of [...this.pendingRequestRejectors]) reject(reason);
  }

  private async restoreAfterReconnect(): Promise<void> {
    if (!this.hasStoredSession()) {
      this.connectionState.set('connected');
      return;
    }
    this.connectionState.set('authenticating');
    try {
      await this.restoreAuthentication();
      this.connectionState.set('connected');
    } catch (error: any) {
      if (error?.code === 'SESSION_EXPIRED') {
        this.connectionState.set('connected');
        for (const listener of [...this.sessionExpiredListeners]) listener();
        return;
      }
      // 一時的な通信障害では保存セッションを捨てず、Socket.IOの再接続を継続する。
      this.connectionState.set('unavailable');
    }
  }

  private async readSessionToken(): Promise<string> {
		const isDesktop = this.desktopBridge.isAvailable();
		// ページ再読み込みではsessionStorageが維持される。同じElectron起動中の
		// セッションまで「起動時認証」として扱わないよう、保存済みトークンを先に使う。
		// Electronを終了して再起動した場合はsessionStorageが空なので、下の起動時認証
		// ポリシーと安全な保存領域からの復元判定へ進む。
		const browserToken = this.readBrowserSessionToken();
		if (browserToken) return browserToken;
		if (!isDesktop) return '';

    if (isDesktop && !this.desktopStartupAuthenticationSatisfied) {
		const [policy, mobilePolicy, sessionPolicy] = await Promise.all([
			this.desktopBridge.getDesktopAuthPolicy(),
			this.desktopBridge.getMobilePasskeyPolicy(),
			this.desktopBridge.getPasskeySessionPolicy(),
		]);
		const allowPasskeySessionRestore = sessionPolicy.ok
			&& sessionPolicy.value?.allowPasskeySessionRestoreAtStartup === true;
		if (!sessionPolicy.ok) {
			this.authenticationRestoreDiagnostic = `セッション維持設定を取得できませんでした（${sessionPolicy.errorCode ?? 'UNKNOWN'}）。`;
		}
		if (
			!allowPasskeySessionRestore
			&& ((policy.ok && policy.value?.requireWindowsHelloAtStartup === true)
				|| (mobilePolicy.ok && mobilePolicy.value?.requireMobilePasskeyAtStartup === true))
		) {
        this.authenticationRestoreDiagnostic = '起動時パスキー認証が必須の設定です。';
        // safeStorage内のトークンは残しても、Windows Helloまたはパスワードで
		// この起動を明示的に認証するまで読み出さない。Macのスマートフォン認証も同様。
        return '';
      }
    }

    const restored = await this.desktopBridge.loadAuthSession();
    const desktopToken = restored.ok && typeof restored.value === 'string' ? restored.value : '';
    if (!desktopToken) {
      this.authenticationRestoreDiagnostic = restored.ok
        ? 'Electronの安全な保存領域にセッションがありません。'
        : `Electronの安全な保存領域を読み出せませんでした（${restored.errorCode ?? 'UNKNOWN'}）。`;
      return '';
    }
		this.authenticationRestoreDiagnostic = 'Electronの安全な保存領域からセッションを読み出しました。';
    try {
      sessionStorage.setItem(this.sessionTokenKey, desktopToken);
    } catch {
      // Electron main側に暗号化済みトークンがあるため、復元処理は継続できる。
    }
    return desktopToken;
  }

  private readBrowserSessionToken(): string {
    try {
      const sessionToken = sessionStorage.getItem(this.sessionTokenKey) ?? '';
      if (sessionToken || this.desktopBridge.isAvailable()) return sessionToken;
      // PWAではアプリ再起動後も、サーバーが許可する有効期限まで復元する。
      return localStorage.getItem(this.sessionTokenKey) ?? '';
    } catch {
      return '';
    }
  }

  private waitForSocketConnection(timeoutMs: number): Promise<void> {
    if (this.socket.connected) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Socketへの再接続を確認できませんでした。'));
      }, timeoutMs);
      const onConnect = () => {
        cleanup();
        resolve();
      };
      const onConnectError = (error: Error) => {
        cleanup();
        reject(error);
      };
      const cleanup = () => {
        clearTimeout(timeout);
        this.socket.off('connect', onConnect);
        this.socket.off('connect_error', onConnectError);
      };
      this.socket.once('connect', onConnect);
      this.socket.once('connect_error', onConnectError);
    });
  }
}
